// lib/sessionMode.ts — who owns local storage right now?
// ─────────────────────────────────────────────────────────────────────────
// PUBLIC-ROUTE FUNNEL (Pass 1). Anonymous visitors and logged-in users must
// never share localStorage keys: before this module existed, a logged-out
// visitor on a shared machine could SEE the previous user's numbers
// (fetchFinancialProfile read `nisk_financial` unconditionally) and their
// edits would overwrite them (localStorageMiddleware wrote the same key).
//
// The rule: one synchronous predicate, consulted by the redux middleware
// and the financial/scenario thunks at every read and write. Anonymous sessions read and write `*_anon`
// keys; authed sessions use the original keys. The two worlds never touch
// except through the explicit adopt/discard calls used by the signup
// carry-over prompt.
//
// Anonymity is derived SYNCHRONOUSLY from Firebase's auth.currentUser —
// never from a flag set by React effects. Effects run child-first, so a
// gated page's mount fetch could otherwise fire before any bridge effect
// updated a flag, reading the wrong world at logged-in boot. currentUser
// is populated synchronously once Firebase resolves, and every gated page
// mounts strictly after that (RequireAuth waits on auth loading). Before
// resolve currentUser is null → the safe ANONYMOUS default: a pre-resolve
// write can only land in the anon keys, never corrupting a real profile.
// Public pages that fetch (Simulate) gate their first fetch on auth
// loading so they never read pre-resolve.
//
// ─────────────────────────────────────────────────────────────────────────
// ADOPTION REWRITTEN 2026-08-01. Two defects in the previous version, and
// both were in the code rather than in its absence.
//
// 1. THE CLEAR RAN EVEN WHEN THE WRITE FAILED. `adoptAnonData` wrapped its
//    two `setItem` calls in a try/catch and then called `discardAnonData()`
//    OUTSIDE it. So on a quota error or in private browsing, the copy threw,
//    the catch swallowed it, and the anonymous data was deleted anyway. The
//    comment on that catch read "the account simply starts fresh", which is
//    an accurate description of losing everything the person had built. That
//    is rule 3 violated in the one place it exists to hold.
//
// 2. IT REPLACED RATHER THAN MERGED. The docstring reasoned that a new
//    account has no local data so a straight copy is correct, and that
//    anything already under the account keys "belonged to a different login
//    and is superseded". Under the 2026-08-01 ruling that is no longer true:
//    the conflict case is real, the person is ASKED, and the prompt promises
//    "your existing plans stay exactly as they are". A `setItem` of the
//    anonymous payload over the signed-in one makes that sentence false.
//
// The sequence is now read, write, verify, clear, in that order, and it
// REPORTS. `anonAdoption.ts` owns the ordering and the outcome shape; this
// module performs the steps. A `void` return could not express any of it,
// because the caller could not tell a good write from a silent failure.
// ─────────────────────────────────────────────────────────────────────────

import { auth } from './firebase';
import {
	adoptionOutcome,
	type AdoptionOutcome,
	type AdoptionStep,
} from './anonAdoption';

export const FINANCIAL_KEY = 'nisk_financial';
export const ANON_FINANCIAL_KEY = 'nisk_financial_anon';
export const SCENARIOS_KEY = 'nisk_scenarios';
export const ANON_SCENARIOS_KEY = 'nisk_scenarios_anon';

export const isAnonymousSession = (): boolean => auth.currentUser === null;

export const activeFinancialKey = (): string =>
	isAnonymousSession() ? ANON_FINANCIAL_KEY : FINANCIAL_KEY;

export const activeScenariosKey = (): string =>
	isAnonymousSession() ? ANON_SCENARIOS_KEY : SCENARIOS_KEY;

// ─── Carry-over (used by the signup prompt in Pass 1 UX) ─────────────────

interface AnonFinancialShape {
	age?: string;
	savings?: string;
	investedSavings?: string;
	incomeEntries?: unknown[];
	expenseEntries?: unknown[];
	holdings?: unknown[];
	equityGrants?: unknown[];
}

interface AnonScenariosShape {
	scenarios?: unknown[];
}

function readJson<T>(key: string): T | null {
	try {
		const raw = localStorage.getItem(key);
		return raw ? (JSON.parse(raw) as T) : null;
	} catch {
		return null;
	}
}

/** True when the anonymous session holds anything worth keeping — numbers
 *  the visitor typed or plans they saved. Zero-state noise doesn't count. */
export function hasAnonData(): boolean {
	const fin = readJson<AnonFinancialShape>(ANON_FINANCIAL_KEY);
	if (fin) {
		const numeric = (v: string | undefined): number => {
			const n = parseFloat(v ?? '');
			return Number.isFinite(n) ? n : 0;
		};
		if (
			(fin.incomeEntries?.length ?? 0) > 0 ||
			(fin.expenseEntries?.length ?? 0) > 0 ||
			(fin.holdings?.length ?? 0) > 0 ||
			(fin.equityGrants?.length ?? 0) > 0 ||
			numeric(fin.savings) > 0 ||
			numeric(fin.investedSavings) > 0 ||
			(fin.age ?? '').trim() !== ''
		) {
			return true;
		}
	}
	const sc = readJson<AnonScenariosShape>(ANON_SCENARIOS_KEY);
	return (sc?.scenarios?.length ?? 0) > 0;
}

/** How many plans are waiting in the anonymous world.
 *
 *  Separate from `hasAnonData` because the prompt states the number: "the
 *  plan you built" and "the 4 plans you built" are different decisions. A
 *  boolean plus a second parse in the bridge would be two readers of one
 *  payload, which is how they come to disagree. */
export function anonPlanCount(): number {
	return readJson<AnonScenariosShape>(ANON_SCENARIOS_KEY)?.scenarios?.length ?? 0;
}

/** Plans already in the SIGNED-IN world. */
function signedInPlans(): unknown[] {
	return readJson<AnonScenariosShape>(SCENARIOS_KEY)?.scenarios ?? [];
}

function idOf(plan: unknown): string | null {
	if (typeof plan !== 'object' || plan === null) return null;
	const id = (plan as { id?: unknown }).id;
	return typeof id === 'string' ? id : null;
}

/**
 * Adopt the anonymous world into the account keys.
 *
 * READ, WRITE, VERIFY, CLEAR. The order is `ADOPTION_SEQUENCE` in
 * `anonAdoption.ts` and it is not an implementation preference: a storage
 * write can fail silently, so clearing before reading back loses the plan
 * every time it does.
 *
 * MERGES, never replaces. Anonymous plans are appended to whatever the
 * account already holds, and a plan id present on both sides keeps the
 * SIGNED-IN copy: that one has been through the server, and the anonymous
 * one is by definition local-only. Colliding ids should be impossible since
 * ids are minted randomly, but "impossible" is not a reason to pick the
 * riskier branch when the two cost the same.
 *
 * FINANCIAL NUMBERS ARE ADOPTED ONLY INTO AN EMPTY ACCOUNT. Plans merge
 * because a person can hold several; a financial profile is singular, so
 * merging two would mean inventing a rule for whose income wins. If the
 * account already has numbers, they stay and only the plans come across.
 * The prompt promises existing plans are untouched; silently rewriting the
 * income behind them would be a larger version of the same broken promise.
 */
export function adoptAnonData(): AdoptionOutcome {
	let step: AdoptionStep = 'read';
	let plansMoved = 0;

	try {
		// ── read ──
		const anonScenarios = readJson<AnonScenariosShape>(ANON_SCENARIOS_KEY);
		const anonPlans = anonScenarios?.scenarios ?? [];
		const anonFinRaw = localStorage.getItem(ANON_FINANCIAL_KEY);

		// ── write ──
		step = 'write';
		const existing = signedInPlans();
		const existingIds = new Set(
			existing.map(idOf).filter((id): id is string => id !== null),
		);
		const incoming = anonPlans.filter((p: unknown): boolean => {
			const id = idOf(p);
			return id === null || !existingIds.has(id);
		});
		const merged = [...existing, ...incoming];
		plansMoved = incoming.length;

		if (merged.length > 0) {
			localStorage.setItem(
				SCENARIOS_KEY,
				JSON.stringify({ ...(readJson<object>(SCENARIOS_KEY) ?? {}), scenarios: merged }),
			);
		}
		// Only into an empty account. See the docstring.
		if (anonFinRaw !== null && localStorage.getItem(FINANCIAL_KEY) === null) {
			localStorage.setItem(FINANCIAL_KEY, anonFinRaw);
		}

		// ── verify ──
		// Read it BACK. A truncated write parses as valid JSON and contains
		// nothing, and private browsing throws on use rather than on probe.
		step = 'verify';
		const writtenIds = new Set(
			signedInPlans().map(idOf).filter((id): id is string => id !== null),
		);
		for (const p of incoming) {
			const id = idOf(p);
			if (id !== null && !writtenIds.has(id)) {
				return adoptionOutcome({ failedAt: 'verify', plansMoved: 0 });
			}
		}

		// ── clear ──
		// Only now. Everything above is reversible; this is not.
		step = 'clear';
		discardAnonData();
	} catch {
		// The anonymous keys are untouched unless we reached `clear`, and by
		// then the plans are already safe on the account side. Rule 4.
		return adoptionOutcome({ failedAt: step, plansMoved: 0 });
	}

	return adoptionOutcome({ failedAt: null, plansMoved });
}

/** Drop the anonymous world ("start fresh" → yes, or logout hygiene). */
export function discardAnonData(): void {
	try {
		localStorage.removeItem(ANON_FINANCIAL_KEY);
		localStorage.removeItem(ANON_SCENARIOS_KEY);
	} catch {
		/* ignore */
	}
}

// ─── The one-time conflict answer ────────────────────────────────────────

const RESOLVED_PREFIX = 'nisk_anon_conflict_resolved:';

/** Per ACCOUNT, not per device. The question is about this account's plans,
 *  so a person who answered on their laptop should not be asked again on
 *  their phone. localStorage is per device, so this is the best available
 *  approximation and the failure mode is one extra prompt on a second
 *  device: annoying, never lossy. */
export function conflictResolved(uid: string): boolean {
	try {
		return localStorage.getItem(RESOLVED_PREFIX + uid) !== null;
	} catch {
		// Unreadable storage means we cannot know the answer. Treating that
		// as "already resolved" would silently strand the plan, so it reads
		// as unresolved and the person is asked. A repeated question is a
		// far cheaper failure than a lost plan.
		return false;
	}
}

export function markConflictResolved(uid: string): void {
	try {
		localStorage.setItem(RESOLVED_PREFIX + uid, String(Date.now()));
	} catch {
		/* ignore: the cost is being asked again, not losing anything */
	}
}
